/**
 * The build hotbar (doc 11 §Input: 1–0). Labels, tooltips and costs resolved
 * from content — exact numbers everywhere, per the tone contract.
 */

import { S } from '../content/strings.ts';
import towersJson from '../content/towers.json' with { type: 'json' };
import { placeableDef } from '../sim/defs.ts';
import type { HotbarItem } from '../ui/hud.ts';

export const BUILD_ORDER = [
  'stabby',
  'zappy_boi',
  'yeet_cannon',
  'laser_pointer',
  'fan_enjoyer',
  'subscription_canceller',
  'wall',
  'ore_rig',
  'smelter',
  'bullet_kitchen',
  'gas_tap',
  'refinery',
  'generator',
  'power_pylon',
  'storage_depot',
  'repair_bay',
] as const;

const LABELS = [S.hotbar.stab, S.hotbar.zap, S.hotbar.yeet, S.hotbar.lasr, S.hotbar.fan, S.hotbar.cncl, S.hotbar.wall, S.hotbar.rig, S.hotbar.melt, S.hotbar.cook, S.hotbar.tap, S.hotbar.refn, S.hotbar.gen, S.hotbar.pyl, S.hotbar.depo, S.hotbar.spa];

const EFFECTS: Record<string, string> = {
  stabby: towersJson.towers.stabby.tagline,
  zappy_boi: towersJson.towers.zappy_boi.tagline,
  yeet_cannon: towersJson.towers.yeet_cannon.tagline,
  laser_pointer: towersJson.towers.laser_pointer.tagline,
  fan_enjoyer: towersJson.towers.fan_enjoyer.tagline,
  subscription_canceller: towersJson.towers.subscription_canceller.tagline,
  wall: 'Professional standing around.',
  ore_rig: 'Hole Enthusiast. Needs a vein. Draws 120 ore a round.',
  smelter: 'Rock Toaster. Eats 120 ore, pays 60 alloy a round.',
  bullet_kitchen: 'Eats 45 alloy, cooks 90 ammo a round.',
  gas_tap: 'Vent Enjoyer. Needs a vent. Draws 90 gas a round.',
  refinery: 'Bubble Sommelier. 60 gas in, 60 fuel out, a round.',
  generator: 'Angry Kettle. +20 power, burns 30 fuel a round.',
  power_pylon: 'Extension Chord. Relays power, radius 5.',
  storage_depot: 'Hoard Locker. +100 storage per resource.',
  repair_bay: 'Building Spa. Patches 300 HP at every payday.',
};

function costString(def: string): string {
  const d = placeableDef(def)!;
  const parts = Object.entries(d.cost)
    .filter(([, v]) => (v ?? 0) > 0)
    .map(([res, v]) => `${v} ${res.toUpperCase()}`);
  const t = towersJson.towers as Record<string, { damage?: number; range?: number; beamDps?: number; fieldSlow?: number }>;
  if (t[def]?.beamDps !== undefined) {
    parts.push(`${t[def]!.beamDps} DMG/S`, `RANGE ${t[def]!.range}`, `${-d.power} POWER`);
  } else if (t[def]?.fieldSlow !== undefined) {
    parts.push(`${Math.round(t[def]!.fieldSlow! * 100)}% SLOW`, `RADIUS ${t[def]!.range}`, `${-d.power} POWER`);
  } else if (t[def]?.damage !== undefined) {
    parts.push(`${t[def]!.damage} DMG`, `RANGE ${t[def]!.range}`);
  } else if (d.power !== 0) {
    parts.push(`${d.power > 0 ? '+' : ''}${d.power} POWER`);
  }
  return parts.join(' · ');
}

/** 1–9, 0, then the six overflow keys — doc 11's hotkey table. */
const OVERFLOW = ['V', 'B', 'N', 'G', 'H', 'J'];

export function hotkeyOf(index: number): string {
  if (index < 9) return String(index + 1);
  if (index === 9) return '0';
  return OVERFLOW[index - 10] ?? '';
}

export const HOTBAR: HotbarItem[] = BUILD_ORDER.map((def, k) => ({
  label: LABELS[k]!,
  hotkey: hotkeyOf(k),
  affordable: true,
  tooltip: {
    name: placeableDef(def)!.name,
    hotkey: hotkeyOf(k),
    effect: EFFECTS[def]!,
    cost: costString(def),
  },
}));
